import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { formatClientNotes } from './client_notes';
import { formatLocationAddress } from './client_locations_map';
import {
  formatRequestDate, getHomepageRequestCategory, HOMEPAGE_REQUEST_STATUS,
  isInHomepageDateWindow, requestValue
} from './fire_request_dashboard';
import { ServiceRequestsMap } from './service_requests_map';

const FILTERS = ['pending', 'scheduling', 'scheduled', 'dispatched', 'complete'];

const matchesSearch = (request, search) => {
  if (!search) return true;
  return [
    requestValue(request, 3), requestValue(request, 10), requestValue(request, 7),
    formatLocationAddress(requestValue(request, 15)), formatClientNotes(requestValue(request, 26)),
    HOMEPAGE_REQUEST_STATUS[getHomepageRequestCategory(request)].label
  ].some(value => String(value || '').toLowerCase().includes(search));
};

const endSession = dispatch => {
  dispatch({ type: 'LOG_OUT', payload: null });
  sessionStorage.removeItem('currentUser');
  window.location = '/';
};

export const FireRequestsHomepage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [activeFilter, setActiveFilter] = useState('');
  const [search, setSearch] = useState('');
  const [loadState, setLoadState] = useState({ status: 'loading', message: '' });

  useEffect(() => {
    let cancelled = false;
    fetch('/apiserviceTickets?app=fire_pest&homepage=true')
      .then(async response => {
        const data = await response.json().catch(() => ({}));
        if (cancelled) return;
        if (response.status === 401 || data?.sessionExpired) return endSession(dispatch);
        if (!response.ok) throw new Error(data?.message || 'Unable to load service requests.');
        setRequests((Array.isArray(data) ? data : []).filter(request => isInHomepageDateWindow(request)));
        setLoadState({ status: 'ready', message: '' });
      })
      .catch(error => !cancelled && setLoadState({ status: 'error', message: error.message || 'Unable to load service requests.' }));
    return () => { cancelled = true; };
  }, [dispatch]);

  const counts = useMemo(() => Object.fromEntries(FILTERS.map(category => [
    category, requests.filter(request => getHomepageRequestCategory(request) === category).length
  ])), [requests]);

  const categoryRequests = useMemo(() => activeFilter
    ? requests.filter(request => getHomepageRequestCategory(request) === activeFilter)
    : requests, [activeFilter, requests]);

  const visibleRequests = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    return categoryRequests.filter(request => matchesSearch(request, normalizedSearch));
  }, [categoryRequests, search]);

  const openRequest = request => {
    const id = requestValue(request, 3);
    navigate(`/serviceTicket/${id}?app=fire_pest`, { state: { id } });
  };

  return (
    <div className="homepage fireRequestsHomepage">
      <div className="dashboardWelcome"><div><h2>Service requests</h2><p>Requests due within 30 days before or after today.</p></div></div>

      {loadState.status === 'loading' && <div className="serviceRequestsState" role="status">Loading service requests…</div>}
      {loadState.status === 'error' && <div className="serviceRequestsState serviceRequestsError" role="alert">{loadState.message}</div>}
      {loadState.status === 'ready' && <>
        <ServiceRequestsMap requests={categoryRequests} />

        <section className="serviceRequestSummary fireHomepageSummary" aria-label="Service request summary filters">
          {FILTERS.map(category => {
            const presentation = HOMEPAGE_REQUEST_STATUS[category];
            return <button
              type="button" key={category}
              className={`serviceRequestSummaryCard serviceRequestSummaryCard-${category === 'pending' ? 'slate' : category === 'scheduling' ? 'amber' : category === 'scheduled' ? 'purple' : category === 'dispatched' ? 'teal' : 'green'} ${activeFilter === category ? 'active' : ''}`}
              aria-pressed={activeFilter === category}
              onClick={() => setActiveFilter(current => current === category ? '' : category)}
            >
              <span className="serviceRequestSummaryTop"><span>{presentation.label}</span><i aria-hidden="true" /></span>
              <strong>{counts[category]}</strong>
              <small>{category === 'pending' ? 'Received or follow-up is 30 days ahead' : category === 'scheduled' ? 'A vendor has been assigned' : category === 'dispatched' ? 'A service provider has been dispatched' : category === 'complete' ? 'Inspection date recorded' : 'Within the scheduling window'}</small>
            </button>;
          })}
        </section>

        <section className="dashboardTableCard fireHomepageRequestList" aria-labelledby="homepage-requests-heading">
          <div className="serviceRequestsToolbar">
            <div><h3 id="homepage-requests-heading">{activeFilter ? HOMEPAGE_REQUEST_STATUS[activeFilter].label : 'Requests in range'}</h3><p>{visibleRequests.length} request{visibleRequests.length === 1 ? '' : 's'} shown</p></div>
            <label className="ticketsInputWrapper"><span className="srOnly">Search homepage service requests</span><input aria-label="Search homepage service requests" placeholder="Search location, trade, status, or notes…" value={search} onChange={event => setSearch(event.target.value)} /></label>
          </div>
          <div className="tableScroll"><table id="tableWrapper" className="serviceRequestsTable">
            <thead><tr><th>Request</th><th>Location Code</th><th>Address</th><th>Trade</th><th>Follow-up Date</th><th>Inspection Date</th><th>Status</th><th>Notes</th></tr></thead>
            <tbody>
              {visibleRequests.map(request => {
                const id = requestValue(request, 3);
                const category = getHomepageRequestCategory(request);
                return <tr key={id} className="tableRow" onClick={() => openRequest(request)}>
                  <td className="tableCell serviceRequestLocationCode">#{id}</td>
                  <td className="tableCell serviceRequestLocationCode">{requestValue(request, 10)}</td>
                  <td className="tableCell">{formatLocationAddress(requestValue(request, 15))}</td>
                  <td className="tableCell">{requestValue(request, 7)}</td>
                  <td className="tableCell">{formatRequestDate(requestValue(request, 32))}</td>
                  <td className="tableCell">{formatRequestDate(requestValue(request, 38))}</td>
                  <td className="tableCell"><span className={`serviceRequestStatus serviceRequestStatus-${category}`}>{HOMEPAGE_REQUEST_STATUS[category].label}</span></td>
                  <td className="tableCell serviceRequestNotesCell">{formatClientNotes(requestValue(request, 26))}</td>
                </tr>;
              })}
              {!visibleRequests.length && <tr><td colSpan="8" className="serviceRequestsEmpty">No requests match this filter.</td></tr>}
            </tbody>
          </table></div>
        </section>
      </>}
    </div>
  );
};
